// The thumbnail a queued photo (or a video's poster) carries to the media ingest (BL-2815, ADR 074 —
// Upload Standard): made when the photo is sent, not when it was picked. Always a JPEG of at most
// 100 KiB, because the ingest refuses anything else.
import { resizeImage } from './resize-image';

/** The longest edge of the thumbnail, in pixels. */
export const SEND_THUMBNAIL_EDGE_PX = 256;
/** The largest thumbnail the ingest accepts. */
export const SEND_THUMBNAIL_MAX_BYTES = 100 * 1024;
/** JPEG qualities tried in order; the first result within the limit wins. */
const QUALITIES = [0.75, 0.65, 0.55, 0.45, 0.35, 0.25];
/** Edges tried after the lowest quality still comes out over the limit. */
const SMALLER_EDGES = [192, 128, 96];

type Encode = (input: { image: Blob; maxEdge: number; quality: number; alwaysEncode: true }) => Promise<Blob | null>;

/**
 * Make the thumbnail for one staged image.
 *
 * @param image - the staged photo's original bytes, or a video's poster frame.
 * @param encode - the canvas re-encode (replaced in tests, which have no canvas).
 * @returns a JPEG of at most {@link SEND_THUMBNAIL_MAX_BYTES}, 256 px on its longest edge or smaller.
 * @throws when the image cannot be decoded or no setting brings it under the limit.
 */
export async function makeSendThumbnail(image: Blob, encode: Encode = resizeImage as Encode): Promise<Blob> {
  const attempts = [
    ...QUALITIES.map((quality) => ({ maxEdge: SEND_THUMBNAIL_EDGE_PX, quality })),
    ...SMALLER_EDGES.map((maxEdge) => ({ maxEdge, quality: QUALITIES[QUALITIES.length - 1] })),
  ];
  // The first pass decodes the original once. A retry re-encodes that small result, so a large photo is never decoded twice.
  let source = image;
  for (const attempt of attempts) {
    const blob = await encode({ image: source, ...attempt, alwaysEncode: true });
    if (blob != null && blob.size <= SEND_THUMBNAIL_MAX_BYTES) return blob;
    if (blob != null) source = blob;
  }
  throw new Error('makeSendThumbnail: no setting brought the thumbnail under 100 KiB');
}
